// pages/FinancialLiteracyProgram.js - Web version with child-specific debug filtering
import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Play, CheckCircle, Lock, Trophy, Star, Brain, Clock, Target } from 'lucide-react';
import { Button, Card } from '../components/ui';

const FinancialLiteracyProgram = ({ child, family, onNavigate }) => {
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

  // Load progress from database with enhanced debugging
  useEffect(() => {
    if (child?.id) {
      loadProgress();
    } else {
      console.log('No child provided, showing default UI');
      setLoading(false);
    }
    
    // Failsafe timeout - always stop loading after 10 seconds
    const timeout = setTimeout(() => {
      console.log('Timeout reached, stopping loading');
      setLoading(false);
    }, 10000);
    
    return () => clearTimeout(timeout);
  }, [child?.id]);

  const loadProgress = async () => {
    try {
      
      if (!child?.id) {
        console.log('DEBUG: No child ID provided, skipping progress load');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/lessons/progress/${child.id}`);
      console.log('DEBUG: Response status:', response.status);
      
      const result = await response.json();
      console.log('DEBUG: Raw API response:', result);
      console.log('DEBUG: All completions from API:', result.data);
      
      if (result.success && result.data) {
        // Client-side filtering as safety measure
        const childSpecificCompletions = result.data.filter(completion => {
          const completionChildId = completion.child_id;
          const currentChildId = parseInt(child.id);
          
          console.log('DEBUG: Comparing completion child_id:', completionChildId, 'with current child:', currentChildId);
          
          return completionChildId === currentChildId || completionChildId === child.id;
        });
        
        console.log('DEBUG: Child-specific completions after filtering:', childSpecificCompletions);
        console.log('DEBUG: Filtered count:', childSpecificCompletions.length, 'vs original count:', result.data.length);
        
        setCompletions(childSpecificCompletions);
        
        if (childSpecificCompletions.length !== result.data.length) {
          console.warn('WARNING: API returned completions for multiple children! This is a backend bug.');
          console.warn('Original completions:', result.data.map(c => ({ child_id: c.child_id, chapter_id: c.chapter_id, lesson_id: c.lesson_id })));
        }
        
      } else {
        console.log('DEBUG: API returned error or no data:', result.error);
        setCompletions([]);
      }
    } catch (error) {
      console.error('DEBUG: Error loading progress:', error);
      setCompletions([]);
    } finally {
      console.log('DEBUG: Setting loading to false');
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadProgress();
    setRefreshing(false);
  };

  // Calculate chapter progress (matches mobile logic)
  const calculateChapterProgress = (chapterId) => {
    const chapterCompletions = completions.filter(c => c.chapter_id === chapterId);
    console.log(`DEBUG: Chapter ${chapterId} completions:`, chapterCompletions);
    return (chapterCompletions.length / 5) * 100; // 5 lessons per chapter
  };

  // Determine chapter status (matches mobile logic)
  const getChapterStatus = (chapterId) => {
    const progress = calculateChapterProgress(chapterId);
    if (progress === 100) return 'completed';
    if (progress > 0) return 'in-progress';
    
    // Chapter 1 is always unlocked
    if (chapterId === 1) return 'unlocked';
    
    // Other chapters unlock when previous chapter is completed
    const prevChapterProgress = calculateChapterProgress(chapterId - 1);
    return prevChapterProgress === 100 ? 'unlocked' : 'locked';
  };

  // Chapter definitions (matches mobile structure)
  const chapters = [
    {
      id: 1,
      title: "Money Mindset & Goal Setting",
      description: "Plant the seeds of smart money habits and learn to set achievable financial goals",
      icon: "🌱",
      lessons: 5
    },
    {
      id: 2,
      title: "Budgeting Basics", 
      description: "Master the art of tracking income and expenses",
      icon: "💰",
      lessons: 5
    },
    {
      id: 3,
      title: "Saving Strategies",
      description: "Build emergency funds and develop consistent saving habits", 
      icon: "🏦",
      lessons: 5
    },
    {
      id: 4,
      title: "Investment Fundamentals",
      description: "Learn the basics of growing your money through investments",
      icon: "📈", 
      lessons: 6
    },
    {
      id: 5,
      title: "Debt Management",
      description: "Strategies to tackle and eliminate debt effectively",
      icon: "🎯",
      lessons: 5
    },
    // Additional chapters (locked for now)
    ...Array.from({ length: 15 }, (_, i) => ({
      id: i + 6,
      title: `Advanced Topic ${i + 1}`,
      description: `Coming soon - Advanced financial concepts`,
      icon: "🔒",
      lessons: 5
    }))
  ];

  // Calculate dynamic values
  const chaptersWithProgress = chapters.map(chapter => ({
    ...chapter,
    progress: calculateChapterProgress(chapter.id),
    status: getChapterStatus(chapter.id)
  }));

  const totalLessons = chapters.reduce((sum, chapter) => sum + chapter.lessons, 0);
  const totalCompletedLessons = completions.length;
  const overallProgress = totalLessons > 0 ? (totalCompletedLessons / totalLessons) * 100 : 0;
  const unlockedChapters = chaptersWithProgress.filter(c => c.status !== 'locked').length;

  // Helper functions for UI
  const getStatusIcon = (status, progress) => {
    if (status === "locked") return <Lock className="h-5 w-5 text-gray-400" />;
    if (progress === 100) return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (progress > 0) return <Clock className="h-5 w-5 text-blue-600" />;
    return <Play className="h-5 w-5 text-green-600" />;
  };

  const getStatusBadge = (status, progress) => {
    if (status === "locked") return "Locked";
    if (progress === 100) return "Complete";
    if (progress > 0) return `${Math.round(progress)}%`;
    return "Start";
  };

  const getStatusColor = (status, progress) => {
    if (status === "locked") return "bg-gray-100 text-gray-600";
    if (progress === 100) return "bg-green-100 text-green-800";
    if (progress > 0) return "bg-blue-100 text-blue-800"; 
    return "bg-emerald-100 text-emerald-800";
  };

  const handleChapterClick = (chapterId, status) => {
    if (status === "locked") {
      alert("Complete the previous chapter to unlock this one.");
      return;
    }
    
    if (onNavigate) {
      onNavigate('financialLiteracyChapter', { 
        chapterId: chapterId.toString(),
        child: child,
        family: family
      });
    }
  };

  const handleBackToMain = () => {
    if (onNavigate) {
      onNavigate('childDashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading Financial Literacy Program...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleBackToMain}
            className="flex items-center gap-2 bg-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>

        {/* Debug Info - Remove in production */}


        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center items-center mb-6">
            <div className="bg-white rounded-full p-4 shadow-lg">
              <BookOpen className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-green-800 mb-4">
            Financial Literacy Program
          </h1>
          <p className="text-xl text-green-700 max-w-3xl mx-auto">
            Master your money with our comprehensive program. Build financial confidence through interactive lessons and practical exercises.
          </p>
        </div>

        {/* Progress Overview */}
        <Card className="p-6 mb-8 bg-white/80 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-green-800">
              {child?.first_name || child?.firstName || 'Your'} Progress
            </h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onRefresh}
              disabled={refreshing}
              className="text-green-600 hover:text-green-700"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-1 bg-gray-200 rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-green-400 to-emerald-500 h-4 rounded-full transition-all duration-500"
                style={{ width: `${Math.max(overallProgress, 2)}%` }}
              ></div>
            </div>
            <span className="text-lg font-medium text-green-700 min-w-[60px]">
              {Math.round(overallProgress)}%
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-600 mb-1">{totalCompletedLessons}</div>
              <div className="text-sm text-green-700">Lessons Completed</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600 mb-1">{unlockedChapters}</div>
              <div className="text-sm text-blue-700">Chapters Unlocked</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-600 mb-1">{totalLessons}</div>
              <div className="text-sm text-purple-700">Total Lessons</div>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="text-2xl font-bold text-yellow-600 mb-1">
                {chaptersWithProgress.filter(c => c.progress === 100).length}
              </div>
              <div className="text-sm text-yellow-700">Chapters Complete</div>
            </div>
          </div>
        </Card>

        {/* Chapters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {chaptersWithProgress.map((chapter) => (
            <Card 
              key={chapter.id}
              className={`transition-all duration-200 cursor-pointer hover:shadow-xl ${
                chapter.status === "locked" 
                  ? 'opacity-60 cursor-not-allowed bg-gray-50' 
                  : chapter.progress === 100
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 hover:-translate-y-1'
                  : 'bg-white hover:-translate-y-1 hover:shadow-lg'
              }`}
              onClick={() => handleChapterClick(chapter.id, chapter.status)}
            >
              <div className="p-6">
                {/* Chapter Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`text-3xl p-2 rounded-lg ${
                      chapter.status === 'locked' ? 'bg-gray-100' : 
                      chapter.progress === 100 ? 'bg-green-100' : 'bg-blue-50'
                    }`}>
                      {chapter.icon}
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 font-medium">Chapter {chapter.id}</div>
                      {getStatusIcon(chapter.status, chapter.progress)}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(chapter.status, chapter.progress)}`}>
                    {getStatusBadge(chapter.status, chapter.progress)}
                  </span>
                </div>

                {/* Chapter Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {chapter.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {chapter.description}
                </p>

                {/* Progress Bar for In-Progress Chapters */}
                {chapter.progress > 0 && chapter.progress < 100 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{Math.round(chapter.progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${chapter.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Chapter Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center space-x-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{chapter.lessons} lessons</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>~{chapter.lessons * 12}min</span>
                    </span>
                  </div>
                  
                  {chapter.status !== 'locked' && (
                    <Button 
                      size="sm" 
                      variant={chapter.progress === 100 ? 'secondary' : 'default'}
                      className={
                        chapter.progress === 100 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChapterClick(chapter.id, chapter.status);
                      }}
                    >
                      {chapter.progress === 100 ? 'Review' : chapter.progress > 0 ? 'Continue' : 'Start'}
                    </Button>
                  )}
                </div>

                {/* Locked Message */}
                {chapter.status === 'locked' && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Lock className="w-4 h-4" />
                      <span>Complete Chapter {chapter.id - 1} to unlock</span>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Encouragement Section */}
        {overallProgress > 0 && (
          <Card className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <div className="text-center">
              <div className="text-4xl mb-3">🎉</div>
              <h3 className="text-xl font-bold text-purple-900 mb-2">
                Keep Up the Great Work!
              </h3>
              <p className="text-purple-700 mb-4">
                You've completed {totalCompletedLessons} lessons and are {Math.round(overallProgress)}% through the program.
                {overallProgress < 25 && " You're just getting started - every lesson brings you closer to financial mastery!"}
                {overallProgress >= 25 && overallProgress < 50 && " You're building momentum - great job!"}
                {overallProgress >= 50 && overallProgress < 75 && " You're over halfway there - fantastic progress!"}
                {overallProgress >= 75 && " You're almost done - finish strong!"}
              </p>
              
              {/* Next suggested action */}
              {(() => {
                const nextChapter = chaptersWithProgress.find(c => c.status === 'unlocked' && c.progress < 100);
                if (nextChapter) {
                  return (
                    <Button 
                      onClick={() => handleChapterClick(nextChapter.id, nextChapter.status)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Continue with Chapter {nextChapter.id}: {nextChapter.title}
                    </Button>
                  );
                }
                return null;
              })()}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FinancialLiteracyProgram;